import { useEffect, useRef, useState } from "react"

export const HEADER_MEDIA_COLLAPSE_THRESHOLD = 24
export const HEADER_MEDIA_REMOUNT_MEMORY_MS = 1500
const HEADER_MEDIA_TRANSITION_MS = 200
const collapsedHeaderMediaByPage = new Map()

export function getHeaderMediaCollapseState(currentCollapsed, scrollTop, threshold = HEADER_MEDIA_COLLAPSE_THRESHOLD) {
    if (scrollTop > threshold) return true
    if (scrollTop <= 0) return false
    return currentCollapsed
}

export function useHeaderMediaCollapse({ disabled } = {}) {
    const pageKey = getPageKey()
    const [collapsed, setCollapsed] = useState(() => hasRememberedCollapsedHeaderMedia(pageKey))
    const headerRef = useRef(null)
    const collapsedRef = useRef(collapsed)

    useEffect(setupScrollListener, [disabled])

    return { collapsed, headerRef }

    function setupScrollListener() {
        if (disabled || typeof document === "undefined") {
            setCollapsedState(false)
            return undefined
        }

        document.addEventListener("scroll", handleScroll, true)
        document.addEventListener("wheel", handleWheel, { capture: true, passive: true })
        window.addEventListener("scroll", handleScroll, { passive: true })
        return cleanup
    }

    function cleanup() {
        document.removeEventListener("scroll", handleScroll, true)
        document.removeEventListener("wheel", handleWheel, true)
        window.removeEventListener("scroll", handleScroll)
    }

    function handleScroll(event) {
        const target = event?.target ?? window
        if (!isRelatedScrollTarget(headerRef.current, target)) return
        const scrollTop = getScrollTop(target)
        if (scrollTop > HEADER_MEDIA_COLLAPSE_THRESHOLD) {
            setCollapsedState(true, target)
        }
    }

    function handleWheel(event) {
        if (!collapsedRef.current) return
        if (event.deltaY >= 0) return
        const target = getEventTarget(event)
        if (!isRelatedScrollTarget(headerRef.current, target)) return
        expandWhenScrollTargetReachesTop(getWheelScrollTarget(target))
    }

    function setCollapsedState(nextCollapsed) {
        if (collapsedRef.current === nextCollapsed) return
        rememberHeaderMediaCollapse(pageKey, nextCollapsed)
        collapsedRef.current = nextCollapsed
        setCollapsed(nextCollapsed)
        notifyHeaderMediaLayoutChanged()
    }

    function expandWhenScrollTargetReachesTop(target) {
        if (getScrollTop(target) <= 0) {
            setCollapsedState(false)
            return
        }
        window.requestAnimationFrame?.(expandAfterWheelScroll)

        function expandAfterWheelScroll() {
            if (!collapsedRef.current) return
            if (getScrollTop(target) > 0) return
            setCollapsedState(false)
        }
    }
}

export function resetHeaderMediaCollapseStateForTests() {
    collapsedHeaderMediaByPage.clear()
}

export function notifyHeaderMediaLayoutChanged() {
    if (typeof window === "undefined") return
    dispatchResizeEvent()
    window.requestAnimationFrame?.(dispatchResizeEvent)
    window.setTimeout(dispatchResizeEvent, HEADER_MEDIA_TRANSITION_MS)
}

function dispatchResizeEvent() {
    window.dispatchEvent(createResizeEvent())
}

function createResizeEvent() {
    if (typeof Event === "function") return new Event("resize")
    const event = document.createEvent("Event")
    event.initEvent("resize", true, true)
    return event
}

function getPageKey() {
    if (typeof window === "undefined") return "server"
    return `${window.location.pathname}${window.location.search}`
}

function hasRememberedCollapsedHeaderMedia(pageKey) {
    const expiresAt = collapsedHeaderMediaByPage.get(pageKey)
    if (!expiresAt) return false
    if (Date.now() <= expiresAt) return true
    collapsedHeaderMediaByPage.delete(pageKey)
    return false
}

function rememberHeaderMediaCollapse(pageKey, collapsed) {
    if (!collapsed) {
        collapsedHeaderMediaByPage.delete(pageKey)
        return
    }
    collapsedHeaderMediaByPage.set(pageKey, Date.now() + HEADER_MEDIA_REMOUNT_MEMORY_MS)
}

function getScrollTop(target) {
    if (target === window) return window.scrollY ?? window.pageYOffset ?? document.documentElement.scrollTop ?? 0
    if (target === document) return document.documentElement.scrollTop ?? document.body?.scrollTop ?? 0
    return target?.scrollTop ?? 0
}

function getEventTarget(event) {
    if (typeof Node !== "undefined" && event?.target?.nodeType === Node.TEXT_NODE) return event.target.parentElement
    return event?.target
}

function getWheelScrollTarget(target) {
    const scrollTarget = findScrollableTarget(target)
    return scrollTarget ?? target
}

function findScrollableTarget(target) {
    let current = target
    while (current && current !== document.body && current !== document.documentElement) {
        if (isScrollableTarget(current)) return current
        current = current.parentElement
    }
    return undefined
}

function isScrollableTarget(target) {
    if (!target) return false
    if (getScrollTop(target) > 0) return true
    if (target.scrollHeight <= target.clientHeight) return false
    const overflowY = window.getComputedStyle?.(target).overflowY
    return overflowY === "auto" || overflowY === "scroll"
}

function isRelatedScrollTarget(header, target) {
    if (!header) return false
    if (target === window || target === document) return true
    if (!target?.contains || !header.compareDocumentPosition) return false
    if (target.contains(header)) return true
    return !!(header.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING)
}
